import React from "react";
import { AlertCircle, X } from "lucide-react";
import { useMapsStatus } from "@/components/landing/GoogleMapsProvider";

/**
 * Surfaces a clear, actionable banner above the booking form when Google
 * Maps APIs are unreachable. Helps the website operator notice config
 * issues without digging into the browser console.
 */
const MESSAGES = {
  loading: null, // don't show while still probing
  ok: null,
  missing_key: {
    title: "Google Maps key not configured",
    body: "Set REACT_APP_GOOGLE_MAPS_API_KEY in frontend/.env and restart the frontend.",
  },
  api_not_enabled: {
    title: "Google Maps APIs are not enabled on your Cloud project",
    body: "Open Google Cloud Console → APIs & Services → Library and enable: Maps JavaScript API, Places API (New), Directions API, Geocoding API. Also enable Billing on the project. The page will work the moment they're switched on — no redeploy needed.",
  },
  referer_blocked: {
    title: "Your Google Maps API key blocks this domain",
    body: "Open Credentials → your API key → Application restrictions → HTTP referrers, and add this site's domain (e.g. https://*.preview.emergentagent.com/*) and your production domain.",
  },
  billing_disabled: {
    title: "Billing is not enabled for the Google Cloud project",
    body: "Open Google Cloud Console → Billing → Link a billing account. Maps requires billing even within the free $200/month tier.",
  },
  quota: {
    title: "Google Maps quota exceeded",
    body: "Daily request quota has been used up. Check Cloud Console → Quotas and either raise the limit or wait until tomorrow.",
  },
  invalid_key: {
    title: "Invalid Google Maps API key",
    body: "The key in REACT_APP_GOOGLE_MAPS_API_KEY does not match a valid Google Cloud key. Generate a new one in Credentials and update the .env file.",
  },
  unknown_error: {
    title: "Google Maps services are not responding",
    body: "We could not reach Google Maps. Autocomplete + route map are disabled — you can still type addresses manually and submit the booking.",
  },
};

const MapsStatusBanner = () => {
  const { status, message } = useMapsStatus();
  const [dismissed, setDismissed] = React.useState(false);
  const info = MESSAGES[status];
  if (!info || dismissed) return null;
  return (
    <div
      data-testid={`maps-status-banner-${status}`}
      className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"
    >
      <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
        <AlertCircle className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[#0B1120] text-sm">{info.title}</div>
        <p className="mt-1 text-xs text-slate-700 leading-relaxed">{info.body}</p>
        {message && (
          <div className="mt-2 text-[10px] font-mono text-slate-500 break-words">
            Google says: {message}
          </div>
        )}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="text-slate-400 hover:text-slate-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default MapsStatusBanner;
